import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "../lib/api";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { Lightbox } from "../components/site/Lightbox";
import { STUDIO } from "../data/site";
import { ArrowRight, Instagram } from "lucide-react";

export default function Portfolio() {
  const [lightbox, setLightbox] = useState(null);
  const { data: portfolio = [], isLoading } = useQuery({ queryKey: ["portfolio"], queryFn: async () => (await api.get("/portfolio")).data });
  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: async () => (await api.get("/artists")).data });
  const artistMap = Object.fromEntries(artists.map((a) => [a.slug, a.name]));

  return (
    <>
      <Seo title="Portfolio — The Gallery" description="Explore custom tattoo work from Obsidian Art & Tattoo Studio. Black & grey, realism, fine line, traditional, color and more." />

      <div className="h-[88px] lg:h-[96px]" />

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pt-2 flex justify-end">
        <a href={STUDIO.instagram} target="_blank" rel="noreferrer" data-testid="portfolio-instagram" className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.2em] text-violet hover:text-white transition-colors"><Instagram size={15} /> More on Instagram</a>
      </div>

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-16">
        {isLoading ? (
          <p className="text-[#828290] font-mono text-sm">Loading…</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-5">
            {portfolio.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 50}>
                <button onClick={() => setLightbox(fileUrl(p.image))} data-testid={`portfolio-item-${p.id}`} className="mono-hover img-zoom group relative block w-full aspect-[4/5] overflow-hidden border border-ash">
                  <img src={fileUrl(p.image)} alt={`Tattoo work${artistMap[p.artist_slug] ? ` by ${artistMap[p.artist_slug]}` : ""} — Obsidian Art & Tattoo Studio`} loading="eager" className="w-full h-full object-cover" />
                  {artistMap[p.artist_slug] && (
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-obsidian/90 via-obsidian/40 to-transparent text-left pointer-events-none">
                      <span data-testid={`portfolio-artist-${p.id}`} className="font-mono text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.2em] text-white">{artistMap[p.artist_slug]}</span>
                    </div>
                  )}
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-ash">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-24 text-center">
          <h2 className="display text-5xl lg:text-6xl text-white mb-8">Want a piece like this?</h2>
          <Link to="/booking" data-testid="portfolio-request-cta" className="inline-flex items-center gap-3 bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold px-10 py-5 hover:bg-white transition-colors">Request a Piece <ArrowRight size={15} /></Link>
        </div>
      </section>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
